import {
  DOSE_UNITS,
  FREQUENCY_PERIOD_UNITS,
  TIMING_OPTIONS,
  getFrequencyPreset,
} from '@constants/medication-dosage';

import {
  emptyDosageStep,
  type DosageStep,
} from '@utils/fhir-mappers/dosageSteps';
import {
  type ContainedIngredient,
  type MedicationIngredientRow,
  containedToIngredientRows,
  emptyIngredientRow,
} from '@utils/fhir-mappers/medicationIngredients';

import type { CodeableConcept, Ratio } from 'generated/types';

function getTrimmedString(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

/**
 * Takes `unknown` deliberately. Discern can send a non-string here (a JSON
 * scalar, or an array when a label matched more than once), and since the merge
 * runs inside a `useMemo` during render a throw here is an error boundary rather
 * than a bad prefill.
 */
export function normalizeDoseUnit(raw: unknown): string {
  const trimmed = getTrimmedString(raw);
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  const match =
    DOSE_UNITS.find((unit) => unit.value.toLowerCase() === lower) ??
    // Plural OCR spellings ("tablets", "puffs") have no entry of their own in
    // DOSE_UNITS, so an exact match alone drops the unit entirely and the
    // review UI shows a value with no unit (VEG-804).
    (lower.length > 2 && lower.endsWith('s')
      ? DOSE_UNITS.find(
          (unit) => unit.value.toLowerCase() === lower.slice(0, -1)
        )
      : undefined);
  return match?.value ?? '';
}

/**
 * OCR spellings of a period unit, mapped to the codes the form's dropdowns use.
 * A label reads "twice daily" or "every 6 hrs", never "d" or "h".
 */
const PERIOD_UNIT_ALIASES: Record<string, string> = {
  h: 'h',
  hr: 'h',
  hrs: 'h',
  hour: 'h',
  hours: 'h',
  hourly: 'h',
  d: 'd',
  day: 'd',
  days: 'd',
  daily: 'd',
  wk: 'wk',
  week: 'wk',
  weeks: 'wk',
  weekly: 'wk',
  mo: 'mo',
  month: 'mo',
  months: 'mo',
  monthly: 'mo',
};

const PERIOD_UNIT_CODES = new Set<string>(
  FREQUENCY_PERIOD_UNITS.map((u) => u.value)
);

/**
 * Normalize a period unit to a code the form can display. Returns '' for
 * anything unrecognised rather than passing it through, since an unknown code
 * would render as a blank dropdown with no way for a curator to tell why.
 */
export function normalizePeriodUnit(raw: unknown): string {
  const trimmed = getTrimmedString(raw).toLowerCase();
  if (!trimmed) return '';
  const alias = PERIOD_UNIT_ALIASES[trimmed] ?? trimmed;
  return PERIOD_UNIT_CODES.has(alias) ? alias : '';
}

/** OCR spellings of an EventTiming code. */
const TIMING_CODE_ALIASES: Record<string, string> = {
  morn: 'MORN',
  morning: 'MORN',
  am: 'MORN',
  aft: 'AFT',
  afternoon: 'AFT',
  eve: 'EVE',
  evening: 'EVE',
  pm: 'EVE',
  hs: 'HS',
  bed: 'HS',
  bedtime: 'HS',
  night: 'HS',
  nightly: 'HS',
  ac: 'AC',
  'before meals': 'AC',
  'before food': 'AC',
  'before eating': 'AC',
  pc: 'PC',
  'after meals': 'PC',
  'after food': 'PC',
  'with food': 'PC',
  'with meals': 'PC',
};

const TIMING_CODES = new Set<string>(TIMING_OPTIONS.map((o) => o.value));

/**
 * Parse `timing_when` into EventTiming codes. Accepts a delimited list in either
 * codes ("MORN,HS") or words ("morning and at bedtime"), and drops anything it
 * can't place rather than inventing a code.
 */
export function normalizeTimingCodes(raw: unknown): string[] {
  const trimmed = getTrimmedString(raw);
  if (!trimmed) return [];
  const codes = trimmed
    .split(/[,;/|]|\band\b|\+/i)
    .map((part) =>
      part
        .trim()
        .toLowerCase()
        // Drop leading filler so "at bedtime" and "in the morning" resolve. Only
        // these three — "before"/"after" carry meaning (AC vs PC).
        .replace(/^(at|in the|the)\s+/, '')
    )
    .filter(Boolean)
    .map((part) => TIMING_CODE_ALIASES[part] ?? part.toUpperCase())
    .filter((code) => TIMING_CODES.has(code));
  return [...new Set(codes)];
}

function parseAsNeeded(value: unknown): boolean | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'boolean') return value;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
    return true;
  }
  if (normalized === 'false' || normalized === '0' || normalized === 'no') {
    return false;
  }
  return undefined;
}

function toNumberOrUndefined(value: string): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const asFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number')
    return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

function sanitizeConcept(raw: unknown): CodeableConcept | undefined {
  if (!isRecord(raw)) return undefined;
  const text = asString(raw.text);
  const coding = Array.isArray(raw.coding)
    ? raw.coding.filter(isRecord).map((c) => ({
        ...(asString(c.system) && { system: asString(c.system) }),
        ...(asString(c.code) && { code: asString(c.code) }),
        ...(asString(c.display) && { display: asString(c.display) }),
      }))
    : [];
  if (!text && !coding.length) return undefined;
  return { ...(text && { text }), ...(coding.length && { coding }) };
}

function sanitizeQuantity(raw: unknown) {
  if (!isRecord(raw)) return undefined;
  const value = asFiniteNumber(raw.value);
  const unit = asString(raw.unit);
  if (value === undefined && !unit) return undefined;
  return {
    ...(value !== undefined && { value }),
    ...(unit && { unit }),
    ...(asString(raw.system) && { system: asString(raw.system) }),
    ...(asString(raw.code) && { code: asString(raw.code) }),
  };
}

function sanitizeRatio(raw: unknown): Ratio | undefined {
  if (!isRecord(raw)) return undefined;
  const numerator = sanitizeQuantity(raw.numerator);
  const denominator = sanitizeQuantity(raw.denominator);
  if (!numerator && !denominator) return undefined;
  return {
    ...(numerator && { numerator }),
    ...(denominator && { denominator }),
  };
}

/**
 * Coerce one entry of a discern `ingredient[]` into a shape the read mapper can
 * safely consume. The read mappers are written for server data and assume
 * `text` is a string and `coding` is an array; ML output honours neither, and a
 * throw here takes down the curation screen rather than degrading the prefill.
 */
function sanitizeIngredient(raw: unknown): ContainedIngredient | undefined {
  if (!isRecord(raw)) return undefined;
  const itemCodeableConcept = sanitizeConcept(raw.itemCodeableConcept);
  const strength = sanitizeRatio(raw.strength);
  if (!itemCodeableConcept && !strength) return undefined;
  return {
    ...(itemCodeableConcept && { itemCodeableConcept }),
    ...(strength && { strength }),
    ...(typeof raw.isActive === 'boolean' && { isActive: raw.isActive }),
  };
}

/** An entry this assembler already produced on a previous pass. */
const isIngredientRow = (value: unknown): value is MedicationIngredientRow =>
  isRecord(value) &&
  typeof value.name === 'string' &&
  typeof value.strengthValue === 'string' &&
  typeof value.strengthUnit === 'string';

/**
 * `ingredients` as discern's FHIR output shapes it, a `Medication.ingredient[]`,
 * either as a real array or as JSON that came through a text field.
 *
 * Idempotent: `mergeOcrFromConfig` runs twice on the same record (once in
 * `buildAutoCurationRecord`, again in `ResourceFormWrapper`), so on the second
 * pass this receives the rows the first pass built. Re-reading those as FHIR
 * would yield a blank row per ingredient, and the blanks would win the spread.
 */
function parseIngredients(value: unknown): MedicationIngredientRow[] {
  let raw: unknown = value;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return [];
    try {
      raw = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }
  const list = Array.isArray(raw) ? raw : [raw];

  // Second pass: hand back what the first pass built, untouched.
  const alreadyRows = list.filter(isIngredientRow);
  if (alreadyRows.length === list.length && list.length > 0) return alreadyRows;

  const ingredient = list
    .map(sanitizeIngredient)
    .filter((entry): entry is ContainedIngredient => entry !== undefined);
  if (!ingredient.length) return [];

  // Reuse the read mapper so strengths (and their denominators) hydrate the same
  // way as a stored record's, and each row keeps its source for the later save.
  return containedToIngredientRows([
    { resourceType: 'Medication', ingredient },
  ]).map((row) => ({
    ...row,
    // Keep an unrecognised unit rather than blanking it: it still round-trips to
    // FHIR correctly, it just won't be preselected in the dropdown.
    strengthUnit: normalizeDoseUnit(row.strengthUnit) || row.strengthUnit,
  }));
}

/**
 * `strength_text` when there is no structured `ingredients`. "875/125 mg" gives
 * one row per strength; the names come from the medication title on save, so
 * there is nothing left for the curator to fill in. Returns no rows when nothing
 * numeric can be found, so free text falls through to the note.
 */
export function parseStrengthText(raw: unknown): MedicationIngredientRow[] {
  const trimmed = getTrimmedString(raw);
  if (!trimmed) return [];

  // Anchored to the one shape this was written for: numbers separated by slashes
  // then a single trailing unit, and nothing else. The old trailing-token match
  // took the last alpha word as the unit for every number in the string, so
  // "100 mg tablet" became "100 tablet" and "1,000 mg" became two rows. In
  // auto-curation a confidently wrong strength is worse than an empty field.
  const match =
    /^(\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)*)\s*([a-zA-Z%[\]]+)$/.exec(
      trimmed
    );
  if (!match) return [];

  const unit = normalizeDoseUnit(match[2]);
  if (!unit) return [];

  return match[1]
    .split('/')
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => ({
      ...emptyIngredientRow(),
      strengthValue: value,
      strengthUnit: unit,
    }));
}

/** "100 mL per 1 hour" — no form field holds a rate, so it becomes a note. */
function describeRate(flat: Record<string, unknown>): string {
  const value = getTrimmedString(flat.rateValue);
  const unit = getTrimmedString(flat.rateUnit);
  if (!value || !unit) return '';
  const period = getTrimmedString(flat.ratePeriod);
  const periodUnit = getTrimmedString(flat.ratePeriodUnit);
  if (!periodUnit) return `rate ${value} ${unit}`;
  const per = period && period !== '1' ? `${period} ` : '';
  return `rate ${value} ${unit} per ${per}${periodUnit}`;
}

/**
 * A ceiling whose unit or period didn't normalize can't populate the structured
 * fields. It goes to the note rather than vanishing, which is what the rest of
 * this assembler promises.
 */
function describeUnmappableMaxDose(flat: Record<string, unknown>): string {
  const value = getTrimmedString(flat.maxDoseValue);
  if (!value) return '';
  if (
    normalizeDoseUnit(flat.maxDoseUnit) &&
    normalizePeriodUnit(flat.maxDosePeriodUnit)
  ) {
    return '';
  }
  const unit = getTrimmedString(flat.maxDoseUnit);
  const period = getTrimmedString(flat.maxDosePeriodUnit);
  return `max ${value}${unit ? ` ${unit}` : ''}${period ? ` per ${period}` : ''}`;
}

function buildDosageStep(flat: Record<string, unknown>): DosageStep {
  const step = emptyDosageStep();

  const doseUnit = normalizeDoseUnit(flat.doseUnit);
  const doseRangeUnit = normalizeDoseUnit(flat.doseRangeUnit);
  const doseValue = getTrimmedString(flat.doseValue);
  const doseLow = getTrimmedString(flat.doseLow);
  const doseHigh = getTrimmedString(flat.doseHigh);

  if (doseValue && doseUnit) {
    step.doseValue = doseValue;
    step.doseUnit = doseUnit;
  } else if (doseLow && doseHigh && doseRangeUnit) {
    step.doseValue = doseLow;
    step.doseValueHigh = doseHigh;
    step.doseUnit = doseRangeUnit;
  } else if (doseValue) {
    step.doseValue = doseValue;
  }

  const frequencyTimes = getTrimmedString(flat.timingFrequency);
  const frequencyEvery = getTrimmedString(flat.timingPeriod);
  const frequencyEveryMax = getTrimmedString(flat.timingPeriodMax);
  const frequencyUnit = normalizePeriodUnit(flat.timingPeriodUnit);
  const count = toNumberOrUndefined(getTrimmedString(flat.timingCount));
  const hasStructuredTiming = Boolean(
    frequencyTimes || frequencyEvery || frequencyEveryMax || frequencyUnit
  );

  if (frequencyTimes) step.frequencyTimes = frequencyTimes;
  if (frequencyEvery) step.frequencyEvery = frequencyEvery;
  if (frequencyEveryMax) step.frequencyEveryMax = frequencyEveryMax;
  if (frequencyUnit) step.frequencyUnit = frequencyUnit;

  // PRN is additive: an OCR'd label can say both "twice daily" and "as needed",
  // so the toggle and the schedule are read independently.
  if (parseAsNeeded(flat.asNeeded) === true) {
    step.asNeeded = true;
  }
  if (hasStructuredTiming || count !== undefined) {
    step.frequencyPreset = getFrequencyPreset({
      frequency: toNumberOrUndefined(frequencyTimes),
      period: toNumberOrUndefined(frequencyEvery),
      periodMax: toNumberOrUndefined(frequencyEveryMax),
      periodUnit: frequencyUnit || undefined,
      count,
    });
  }

  step.timing = normalizeTimingCodes(flat.timingWhen);

  const durationValue = getTrimmedString(flat.durationValue);
  const durationUnit = normalizePeriodUnit(flat.durationUnit);
  if (durationValue && durationUnit) {
    step.durationValue = durationValue;
    step.durationUnit = durationUnit;
  }

  const maxDoseValue = getTrimmedString(flat.maxDoseValue);
  const maxDoseUnit = normalizeDoseUnit(flat.maxDoseUnit);
  const maxDosePeriodUnit = normalizePeriodUnit(flat.maxDosePeriodUnit);
  if (maxDoseValue && maxDoseUnit && maxDosePeriodUnit) {
    step.maxDoseValue = maxDoseValue;
    step.maxDoseUnit = maxDoseUnit;
    step.maxDosePeriodUnit = maxDosePeriodUnit;
    // Omitted means "per one", so only carry a period count that says otherwise.
    const period = getTrimmedString(flat.maxDosePeriod);
    if (period && period !== '1') step.maxDosePeriodValue = period;
  }

  return step;
}

function hasDosageStepContent(step: DosageStep): boolean {
  return Boolean(
    step.doseValue ||
    step.doseValueHigh ||
    step.doseUnit ||
    step.frequencyPreset ||
    step.asNeeded ||
    step.frequencyTimes ||
    step.frequencyEvery ||
    step.frequencyEveryMax ||
    step.frequencyUnit ||
    step.timing.length > 0 ||
    step.durationValue ||
    step.maxDoseValue ||
    step.asDirected
  );
}

function formatLegacyDose(step: DosageStep): string {
  if (step.doseValue && step.doseValueHigh && step.doseUnit) {
    return `${step.doseValue}-${step.doseValueHigh} ${step.doseUnit}`;
  }
  if (step.doseValue && step.doseUnit) {
    return `${step.doseValue} ${step.doseUnit}`;
  }
  return '';
}

export function assembleMedicationOcrRecord(
  flat: Record<string, unknown>
): Record<string, unknown> {
  const dosageStep = buildDosageStep(flat);

  const ingredients = parseIngredients(flat.ingredients);
  const strengthRows = ingredients.length
    ? []
    : parseStrengthText(flat.strengthText);
  const rows = ingredients.length ? ingredients : strengthRows;

  // Anything the structured form can't hold goes into "As directed" so the
  // curator sees it and can act on it, rather than it being dropped silently.
  const notes = [
    getTrimmedString(flat.dosageText),
    describeRate(flat),
    describeUnmappableMaxDose(flat),
    // A total count above one has no field; count === 1 is the "one time only"
    // preset and buildDosageStep already captured it.
    (() => {
      const count = toNumberOrUndefined(getTrimmedString(flat.timingCount));
      return count !== undefined && count > 1 ? `${count} doses total` : '';
    })(),
    // Free-text strength that couldn't be parsed into rows.
    ingredients.length || strengthRows.length
      ? ''
      : getTrimmedString(flat.strengthText),
  ].filter(Boolean);
  if (notes.length) dosageStep.asDirected = notes.join('; ');

  const dosageSteps = hasDosageStepContent(dosageStep) ? [dosageStep] : [];
  const dose = formatLegacyDose(dosageStep);

  return {
    title: flat.title || flat.medication || '',
    medication: flat.medication || flat.title || '',
    dose,
    route: flat.route || '',
    frequency: flat.frequency || '',
    startingDate: flat.startingDate || '',
    instructions: flat.instructions || '',
    doseForm: getTrimmedString(flat.doseForm),
    ...(rows.length ? { ingredients: rows } : {}),
    ...(dosageSteps.length ? { dosageSteps } : {}),
  };
}
